// Considered: SphereEditor organism — rejected because it renders a full tabbed editor dialog,
// not a lightweight inline expander for completing a small set of required fields.
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAnalytics } from '@center-technologies/analytics';
import { Sphere } from 'types/global';
import './FinishSetupEditorMolecule.scss';

export interface FinishSetupEditorMoleculeProps {
  sphere: Sphere;
  /** Names of required fields that are not yet filled. */
  requiredFields: string[];
  isExpanded: boolean;
  onFinalized: (updatedSphere: Sphere) => void;
  onCollapse: () => void;
}

const FinishSetupEditorMolecule: React.FC<FinishSetupEditorMoleculeProps> = ({
  sphere,
  requiredFields,
  isExpanded,
  onFinalized,
  onCollapse,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(requiredFields.map((f) => [f, ''])),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleSaveAndActivate = useCallback(async () => {
    if (!apiClients?.graphClient || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const resp = await apiClients.graphClient.post<{ sphere?: Sphere }>(`/spheres/${sphere.id}/finalize`, {
        properties: fieldValues,
      });

      if (resp.sphere) {
        logAnalyticsEvent('draft_sphere_finalized', undefined, {
          sphereId: sphere.id,
        });
        onFinalized(resp.sphere);
      } else {
        setError(t('draft.finalizeError'));
      }
    } catch {
      setError(t('draft.finalizeError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [apiClients, isSubmitting, sphere.id, fieldValues, onFinalized, logAnalyticsEvent, t]);

  if (!isExpanded) return null;

  const typeName = sphere.properties.name || t('draft.defaultTypeName');

  return (
    <div className="finish-setup-editor" data-testid="finish-setup-editor">
      <div className="finish-setup-editor__label">
        {t('draft.completeRequired', { typeName })}
      </div>

      <div className="finish-setup-editor__fields">
        {requiredFields.length === 0 ? (
          <p className="finish-setup-editor__no-fields">{t('draft.noRequiredFields')}</p>
        ) : (
          requiredFields.map((fieldName) => (
            <label key={fieldName} className="finish-setup-editor__field">
              <span className="finish-setup-editor__field-label">
                {fieldName}
                <span className="finish-setup-editor__required-marker" aria-hidden>
                  {' '}*
                </span>
              </span>
              <input
                type={/date/i.test(fieldName) ? 'date' : 'text'}
                className="finish-setup-editor__field-input"
                placeholder={t('draft.fieldPlaceholder', { field: fieldName.toLowerCase() })}
                value={fieldValues[fieldName] ?? ''}
                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                disabled={isSubmitting}
              />
            </label>
          ))
        )}

        {error && (
          <span className="finish-setup-editor__error" role="alert">
            {error}
          </span>
        )}

        <div className="finish-setup-editor__actions">
          <button
            type="button"
            className="finish-setup-editor__save-btn"
            onClick={handleSaveAndActivate}
            disabled={isSubmitting}
          >
            <i className="pi pi-check" />
            {t('draft.saveAndActivate')}
          </button>

          <button
            type="button"
            className="finish-setup-editor__later-btn"
            onClick={onCollapse}
            disabled={isSubmitting}
          >
            {t('draft.later')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinishSetupEditorMolecule;
